export const requestIdleCallback =
  "function" == typeof window.requestIdleCallback
    ? window.requestIdleCallback
    : setTimeout;
export const yieldToMainThread = () =>
  void 0 !== scheduler && "yield" in scheduler
    ? scheduler.yield()
    : new Promise((e) => {
      requestAnimationFrame(() => {
        setTimeout(e, 0);
      });
    });
export function isLowPowerDevice() {
  const e = navigator.hardwareConcurrency || 4,
    t = navigator.deviceMemory || 4;
  return Number(e) <= 2 || Number(t) <= 2;
}
export function supportsViewTransitions() {
  return "function" == typeof document.startViewTransition;
}
export const viewTransition = { current: void 0 };
const viewTransitionTypes = {
  "product-grid": async () => {
    const e = document.querySelector(".product-grid"),
      t = [...document.querySelectorAll(".product-grid .product-grid__item")];
    if (e && t.length)
      return (
        await new Promise((n) =>
          requestIdleCallback(() => {
            const r = getCardsToAnimate(e, t);
            (t.forEach((e, t) => {
              t < r
                ? e.style.setProperty(
                  "view-transition-name",
                  `product-card-${e.dataset.productId}`,
                )
                : e.style.setProperty("content-visibility", "hidden");
            }),
              n(null));
          }),
        ),
        () =>
          t.forEach((e) => {
            (e.style.removeProperty("view-transition-name"),
              e.style.removeProperty("content-visibility"));
          })
      );
  },
};
export function startViewTransition(e, t) {
  return !supportsViewTransitions() ||
    isLowPowerDevice() ||
    prefersReducedMotion()
    ? (e(), Promise.resolve())
    : new Promise(async (n) => {
      let r = [];
      if (t)
        for (const e of t)
          if (viewTransitionTypes[e]) {
            const t = await viewTransitionTypes[e]();
            t && r.push(t);
          }
      const o = document.startViewTransition(e);
      (viewTransition.current || (viewTransition.current = o.finished),
        t && t.forEach((e) => o.types.add(e)),
        o.finished.then(() => {
          ((viewTransition.current = void 0), r.forEach((e) => e()), n());
        }));
    });
}
export function fetchConfig(e = "json", t = {}) {
  const n = {
    "Content-Type": "application/json",
    Accept: `application/${e}`,
    ...t.headers,
  };
  return (
    "javascript" === e &&
    ((n["X-Requested-With"] = "XMLHttpRequest"), delete n["Content-Type"]),
    { method: "POST", headers: n, body: t.body }
  );
}
export function debounce(e, t) {
  let n;
  function r(...r) {
    (clearTimeout(n), (n = setTimeout(() => e.apply(this, r), t)));
  }
  return (
    (r.cancel = () => {
      clearTimeout(n);
    }),
    r
  );
}
export function throttle(e, t) {
  let n = 0;
  function r(...r) {
    const o = performance.now();
    o - n >= t && ((n = o), e.apply(this, r));
  }
  return (
    (r.cancel = () => {
      n = performance.now();
    }),
    r
  );
}
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
export function prefersReducedMotion() {
  return reducedMotion.matches;
}
export function normalizeString(e) {
  return e
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}
export function onDocumentLoaded(e) {
  "complete" === document.readyState ? e() : window.addEventListener("load", e);
}
export function onDocumentReady(e) {
  "loading" === document.readyState
    ? document.addEventListener("DOMContentLoaded", e)
    : e();
}
export function removeWillChangeOnAnimationEnd(e) {
  const t = e.target;
  t &&
    t instanceof HTMLElement &&
    (t.style.setProperty("will-change", "unset"),
      t.removeEventListener("animationend", removeWillChangeOnAnimationEnd));
}
export function onAnimationEnd(e, t, n = { subtree: !0 }) {
  const r = (
    Array.isArray(e) ? e.flatMap((e) => e.getAnimations(n)) : e.getAnimations(n)
  ).reduce(
    (e, t) => (t.timeline instanceof DocumentTimeline && e.push(t.finished), e),
    [],
  );
  return Promise.allSettled(r).then(t);
}
export function isClickedOutside(e, t) {
  return e.target instanceof HTMLDialogElement || !(e.target instanceof Element)
    ? !isPointWithinElement(e.clientX, e.clientY, t)
    : !t.contains(e.target);
}
export function isPointWithinElement(e, t, n) {
  const { left: r, right: o, top: i, bottom: s } = n.getBoundingClientRect();
  return e >= r && e <= o && t >= i && t <= s;
}
export const mediaQueryLarge = matchMedia("(min-width: 750px)");
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}
export function isTouchDevice() {
  return "ontouchstart" in window && navigator.maxTouchPoints > 0;
}
export function clamp(e, t, n) {
  return Math.max(t, Math.min(e, n));
}
export function center(e, t) {
  const { left: n, width: r, top: o, height: i } = e.getBoundingClientRect(),
    s = { x: n + r / 2, y: o + i / 2 };
  return t ? s[t] : s;
}
export function start(e, t) {
  const { left: n, top: r } = e.getBoundingClientRect(),
    o = { x: n, y: r };
  return t ? o[t] : o;
}
export function closest(e, t) {
  return e.reduce(function (e, n) {
    return Math.abs(n - t) < Math.abs(e - t) ? n : e;
  });
}
export function preventDefault(e) {
  e.preventDefault();
}
export function getVisibleElements(e, t, n = 1, r) {
  if (!t?.length) return [];
  const o = e.getBoundingClientRect();
  return t.filter((e) => {
    const {
      width: t,
      height: i,
      top: s,
      right: a,
      left: c,
      bottom: u,
    } = e.getBoundingClientRect();
    if (n < 1) {
      const e = Math.max(o.left, c),
        d = Math.min(o.right, a),
        l = Math.max(0, d - e);
      if ("x" === r) return t > 0 && l / t >= n;
      const p = Math.max(o.top, s),
        m = Math.min(o.bottom, u),
        h = Math.max(0, m - p);
      if ("y" === r) return i > 0 && h / i >= n;
      const f = t * i;
      return f > 0 && (l * h) / f >= n;
    }
    const d = c >= o.left && a <= o.right;
    if ("x" === r) return d;
    const l = s >= o.top && u <= o.bottom;
    return ("y" === r || d) && l;
  });
}
export function getIOSVersion() {
  const { userAgent: e } = navigator;
  if (!/(iPhone|iPad)/i.test(e)) return null;
  const t = e.match(/OS ([\d_]+)/)?.[1],
    [n, r] = t?.split("_") || [];
  return t && n
    ? {
      fullString: t.replace("_", "."),
      major: parseInt(n, 10),
      minor: r ? parseInt(r, 10) : 0,
    }
    : null;
}
function getCardsToAnimate(e, t) {
  if (!e || !t || 0 === t.length) return 0;
  const n = t[0];
  if (!n) return 0;
  const r = e.getBoundingClientRect(),
    o = Math.max(0, r.top),
    i = Math.min(window.innerHeight, r.bottom),
    s = Math.round(i - o);
  if (s <= 0) return 0;
  const a = n.querySelector("product-card"),
    c = getComputedStyle(e),
    u =
      a?.refs?.cardGallery?.style.getPropertyValue("--gallery-aspect-ratio") ||
      "";
  let d = parseFloat(u) || 0.5;
  if (u?.includes("/")) {
    const [e = "1", t = "2"] = u.split("/");
    d = parseInt(e, 10) / parseInt(t, 10);
  }
  const l =
    parseInt(
      a?.refs?.productCardLink?.style.getPropertyValue(
        "--product-card-gap",
      ) || "",
    ) || 12,
    p = parseInt(c.getPropertyValue("--product-grid-gap")) || 12,
    m = 2 * ((parseInt(c.fontSize) || 16) + 2),
    h = window.innerWidth < 750,
    f = h ? Math.round((r.width - p) / 2) : 100,
    g = Math.round(f / d) + l + m;
  return (
    (h ? 2 : Math.floor((r.width + p) / (f + p))) * Math.ceil((s - p) / (g + p))
  );
}
export function preloadImage(e) {
  new Image().src = e;
}
export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute("shimmer", "");
  }
}
customElements.get("text-component") ||
  customElements.define("text-component", TextComponent);
export function resetShimmer(e = document.body) {
  e.querySelectorAll("[shimmer]").forEach((e) => e.removeAttribute("shimmer"));
}
export function changeMetaThemeColor(e) {
  const t = document.head.querySelector('meta[name="theme-color"]');
  t && e && t.setAttribute("content", e);
}
export function getViewParameterValue() {
  return new URLSearchParams(window.location.search).get("view");
}
export function parseIntOrDefault(e, t) {
  if (null == e || "" === e) return t;
  const n = parseInt(e.toString());
  return isNaN(n) ? t : n;
}
class Scheduler {
  #e = new Set();
  #t = !1;
  schedule = async (e) => {
    (this.#e.add(e),
      this.#t ||
      ((this.#t = !0),
        viewTransition.current && (await viewTransition.current),
        requestAnimationFrame(this.flush)));
  };
  flush = () => {
    for (const e of this.#e) setTimeout(e, 0);
    (this.#e.clear(), (this.#t = !1));
  };
}
export const scheduler = new Scheduler();
export function oncePerEditorSession(e, t, n) {
  const r = window.Shopify?.designMode,
    o = JSON.parse(e.dataset.shopifyEditorSection || "{}").id,
    i = JSON.parse(e.dataset.shopifyEditorBlock || "{}").id,
    s = `${t}-${o || i}`;
  (r && sessionStorage.getItem(s)) ||
    (n(), r && sessionStorage.setItem(s, "true"));
}
export class ResizeNotifier extends ResizeObserver {
  #n = !1;
  constructor(e) {
    super((t) => {
      if (this.#n) return e(t, this);
      this.#n = !0;
    });
  }
  disconnect() {
    ((this.#n = !1), super.disconnect());
  }
}
export function setHeaderMenuStyle() {
  const e = document.querySelector("#header-component");
  e &&
    window.requestAnimationFrame(() => {
      const t = e?.querySelector("overflow-list"),
        n = t && t.hasAttribute("minimum-reached");
      e.dataset.menuStyle = isTouchDevice() || n ? "drawer" : "menu";
    });
}
export function calculateHeaderGroupHeight(
  e = document.querySelector("#header-component"),
  t = document.querySelector("#header-group"),
) {
  if (!t) return 0;
  let n = 0;
  const r = t.children;
  for (let t = 0; t < r.length; t++) {
    const o = r[t];
    o !== e && o instanceof HTMLElement && (n += o.offsetHeight);
  }
  return e instanceof HTMLElement &&
    e.hasAttribute("transparent") &&
    e.parentElement?.nextElementSibling
    ? n + e.offsetHeight
    : n;
}
function updateTransparentHeaderOffset() {
  const e = document.querySelector("#header-component"),
    t = document.querySelector("#header-group"),
    n = t?.querySelector(".header-section");
  if (!n || !e?.hasAttribute("transparent"))
    return void document.body.style.setProperty(
      "--transparent-header-offset-boolean",
      "0",
    );
  const r = n.nextElementSibling?.classList.contains("shopify-section"),
    o = r ? "0" : "1";
  document.body.style.setProperty("--transparent-header-offset-boolean", o);
}
function updateHeaderHeights() {
  const e = document.querySelector("header-component");
  if (!(e instanceof HTMLElement)) return;
  const t = e.offsetHeight,
    n = calculateHeaderGroupHeight(e),
    r = e.querySelector(".header__row--top");
  (document.body.style.setProperty("--header-height", `${t}px`),
    document.body.style.setProperty("--header-group-height", `${n}px`),
    r &&
    window.requestAnimationFrame(function () {
      e.style.setProperty("--top-row-height", `${r.offsetHeight}px`);
    }));
}
export function updateAllHeaderCustomProperties() {
  (updateHeaderHeights(),
    updateTransparentHeaderOffset(),
    setHeaderMenuStyle());
}
export function escapeHTML(e) {
  if ("string" != typeof e) return String(e);
  const t = document.createElement("p");
  return ((t.textContent = e), t.innerHTML);
}
export function html(e, ...t) {
  return e.reduce((e, n, r) => {
    const o = t[r];
    let i = "";
    return (
      Array.isArray(o)
        ? (i = o.join(""))
        : null != o && (i = escapeHTML(String(o))),
      e + n + i
    );
  }, "");
}
export function formatMoney(cents, format) {
  if (cents === null || cents === undefined) return "";

  if (typeof cents === 'string') {
    cents = cents.replace(/[^0-9]/g, '');
  }

  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || (window.Theme?.settings?.money_format) || '₹{{amount_no_decimals}}';

  function formatWithDelimiters(number, precision = 0, thousands = ',', decimal = '.') {
    if (isNaN(number) || number == null) return '0';
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = (parts[1] && precision > 0) ? decimal + parts[1] : '';
    return dollarsAmount + centsAmount;
  }

  const match = formatString.match(placeholderRegex);
  if (!match) {
    return formatWithDelimiters(cents);
  }

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_trailing_zeros':
      value = formatWithDelimiters(cents, 2).replace(/\.00$/, '');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  const result = formatString.replace(placeholderRegex, value);

  return result
    .replace(/Rs\.\s*/g, '₹')
    .replace('.00', '')
    .trim();
}

"undefined" != typeof Theme &&
  (Theme.utilities = {
    ...Theme.utilities,
    scheduler: scheduler,
    escapeHTML: escapeHTML,
    html: html,
    formatMoney: formatMoney,
  });